import React, { useEffect, useRef, useState } from 'react';
import { Flex, message } from 'antd';
import { SearchBoxWithDropdownBox, filterOptions } from 'src/components/ui';
import { MCustomerDetail } from 'src/components/model/mCustomer/detail';
import { DMList } from 'src/components/model/directMessage/list';
import { Chat } from 'src/components/model/directMessage/chat';
import {
  SearchFilter,
  useMessageRoom,
  useMessageRoomList,
} from 'src/usecases/messageRoom/reader';
import { MessageRoom } from 'src/models/messageRoom';
import { toMCustomerId } from 'src/models/mCustomer';
import { useLocation, useNavigate } from 'react-router-dom';
import RoutePaths from 'src/root/path';
import { getRoomId } from 'src/root/path.function';
import { useMCustomerItem } from 'src/usecases/mCustomer/reader';
import { QueryDocumentSnapshot } from 'firebase/firestore';

// 1ページに表示するデータ数
export const PAGE_SIZE = 50;

export const Message: React.FC = () => {
  const location = useLocation();

  const selectRoomId = getRoomId(location.pathname);

  const { data: room } = useMessageRoom({ mRoomId: selectRoomId });

  const [selectRoom, setSelectRoom] = useState<MessageRoom | null>(null);

  // 検索フィルターの状態管理
  const [searchFilters, setSearchFilters] = useState<SearchFilter[]>([]);

  // ドロップダウンの選択肢の文字列
  const FILTER_ALL_STR = 'all';
  const FILTER_IMPORTANT_STR = 'isImportant';
  const FILTER_NOT_IMPORTANT_STR = 'isNotImportant';
  // ドロップダウンの選択肢
  const options = [
    {value: FILTER_ALL_STR, label: "全て表示"},
    {value: FILTER_IMPORTANT_STR, label: "星マークのみ表示"},
    {value: FILTER_NOT_IMPORTANT_STR, label: "星マーク以外を表示"}
  ];

  useEffect(() => {
    if (room?.room && !selectRoom) {
      setSelectRoom(room.room);
    }
    return () => {};
  }, [room, selectRoom]);

  // URLパラメータからフィルター情報を取得
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('searchQuery');
  const searchField = searchParams.get('searchField') as SearchFilter['field'] | null;
  
  // レガシーサポート：既存の id パラメータ
  const legacySearchId = searchParams.get('id');

  const [selectFilter, setSelectFilter] = useState<string>(FILTER_ALL_STR);
  // 現在選択しているページ
  const [currentPage, setCurrentPage] = useState(1);
  // ページの最初のドキュメント情報
  const firstDoc = useRef<QueryDocumentSnapshot | undefined>(undefined);
  // ページの最後のドキュメント情報
  const lastDoc = useRef<QueryDocumentSnapshot | undefined>(undefined);

  // ぺージングの値をリセットする関数
  const resetPagingStatus = () => {
    setCurrentPage(1);
    firstDoc.current = undefined;
    lastDoc.current = undefined;
  }

  // 複数フィルター検索かどうかの判定
  const isMultipleFilterSearch = () => {
    return searchFilters.length > 1;
  }

  // useMessageRoomListの引数を取得する関数
  const getArgsUseMessageRoomList = () => {
    // フィルターの状態を取得
    let filterStatus: boolean | undefined = undefined;
    if (selectFilter === FILTER_IMPORTANT_STR) {
      // 重要データのみを表示する場合
      filterStatus = true;
    } 
    else if (selectFilter === FILTER_NOT_IMPORTANT_STR) {
      // 重要データ以外を表示する場合
      filterStatus = false;
    }
    
    // 検索フィルターがある場合
    if (searchFilters.length > 0) {
      if (isMultipleFilterSearch()) {
        // 複数フィルター：ページング無効
        return { 
          mCustomerId: null,
          searchFilters: searchFilters,
          isImportant: filterStatus,
          firstDoc: undefined, // 複数フィルター検索時はページング無効
          lastDoc: undefined,  // 複数フィルター検索時はページング無効
        };
      } else {
        // 単一フィルター：ページング有効
        return { 
          mCustomerId: null,
          searchFilters: searchFilters,
          isImportant: filterStatus,
          firstDoc: firstDoc.current,
          lastDoc: lastDoc.current,
        };
      }
    }
    
    // レガシーサポート：既存のid検索
    if (legacySearchId) {
      return { 
        mCustomerId: legacySearchId, 
        isImportant: filterStatus,
        firstDoc: firstDoc.current,
        lastDoc: lastDoc.current,
      };
    }
    
    // 通常検索
    return { 
      mCustomerId: null,
      isImportant: filterStatus,
      firstDoc: firstDoc.current,
      lastDoc: lastDoc.current,
    };
  }
  const { data } = useMessageRoomList(getArgsUseMessageRoomList());

  const { data: selectRoomMCus } = useMCustomerItem({
    mCustomerId: selectRoom?.mCusId
      ? toMCustomerId(selectRoom!.mCusId)
      : undefined,
  });
  

  const navigate = useNavigate();

  const onClick = React.useCallback(
    (room: MessageRoom) => {
      setSelectRoom(room);
      if (!searchFilters.length && !legacySearchId) {
        navigate('/' + RoutePaths.directMessage.path + room.id, {
          replace: true,
        });
      }
    },
    [navigate, searchFilters, legacySearchId]
  );

  // フィルター変更時のハンドラー
  const handleFiltersChange = (filters: SearchFilter[]) => {
    setSearchFilters(filters);
    resetPagingStatus();
    setSelectRoom(null);
    
    // URLパラメータを更新
    const searchParams = new URLSearchParams();
    if (filters.length > 0) {
      // 複数フィルターの場合は最初のフィルターをURLに反映（後で改善予定）
      const firstFilter = filters[0];
      searchParams.set('searchQuery', firstFilter.value);
      searchParams.set('searchField', firstFilter.field);
    }
    
    const queryString = searchParams.toString();
    navigate('/' + RoutePaths.directMessage.path + (queryString ? `?${queryString}` : ''), {
      replace: true,
    });
  };

  // ドロップダウンイベントの関数
  const handleChange = (value: string) => {
    // ドロップダウンの選択肢を変更
    setSelectFilter(value);

    // ページングの値をリセット
    resetPagingStatus();
  }

  // フィルターの有無によって表示するデータを変更
  const returnRoomDatas = () => {
    // 「お気に入りのみ表示」の場合はサーバー側でフィルタリング済みなのでそのまま返す
    if (selectFilter === FILTER_IMPORTANT_STR) {
      return data?.rooms ?? [];
    }
    
    // 「お気に入り以外を表示」の場合はクライアント側でフィルタリング
    if (selectFilter === FILTER_NOT_IMPORTANT_STR) {
      return data?.rooms.filter(room => room.isImportant !== true) ?? [];
    }
    
    // 「全て表示」の場合はそのまま返す
    return data?.rooms ?? [];
  }

  // ページの最終ドキュメントを管理する（複数フィルター時は無効）
  const handlePageChange = (direction: 'prev' | 'next') => {
    // 複数フィルター検索時はページング無効
    if (isMultipleFilterSearch()) {
      return; // 何もしない
    }

    // ドキュメント情報をリセット
    firstDoc.current = undefined;
    lastDoc.current = undefined;

    // 選択したページが現在のページより大きい場合、次のページを取得する
    if (direction === 'next') {
      // 次のページを取得するには、最後のドキュメントを取得する
      lastDoc.current = data?.lastDoc;
      setCurrentPage(currentPage + 1);
    }
    // 選択したページが現在のページより小さい場合、前のページを取得する
    if (direction === 'prev') {
      // 前のページを取得するには、最初のドキュメントを取得する
      // 1ページ目の場合は、最初のドキュメントは存在しないため、undefinedを設定
      firstDoc.current = currentPage - 1 === 1 ? undefined : data?.firstDoc;
      setCurrentPage(currentPage - 1);
    }
  }

  // URLからフィルター情報を復元
  useEffect(() => {
    if (searchQuery && searchField) {
      // 共通のfilterOptionsを使用してラベルを取得
      const filterLabel = filterOptions.find(opt => opt.value === searchField)?.label || '';
      
      const filter: SearchFilter = {
        id: crypto.randomUUID(),
        field: searchField,
        label: filterLabel,
        value: searchQuery
      };
      
      setSearchFilters([filter]);
    }
  }, [searchQuery, searchField]);

  // 警告メッセージの表示（複数フィルター時のみ）
  useEffect(() => {
    if (data?.warning) {
      // 警告メッセージがある場合は3秒間表示
      message.warning(data.warning, 3);
    }
  }, [data]);

  return (
    <div>
      <SearchBoxWithDropdownBox
        onFiltersChange={handleFiltersChange}
        options={options}
        defaultValue={options[0].label}
        handleChange={handleChange}
        searchResultCount={searchFilters.length > 0 ? returnRoomDatas().length : undefined}
      />
      <div style={{ height: '10px' }}></div>
      <Flex justify="space-between" style={{ maxHeight: '75vh' }}>
        <DMList
          totalCount={data?.totalCount ?? 0}
          data={returnRoomDatas()}
          pageSize={PAGE_SIZE}
          selectingRoom={selectRoom}
          onSelect={onClick}
          pageNum={currentPage}
          handlePageChange={handlePageChange}
        />
        <Chat
          selectRoom={{
            mCustomer: selectRoomMCus?.mCustomer ?? null,
            mRoom: selectRoom,
          }}
        />
        <Flex
          style={{
            width: '32%',
            background: 'white',
            borderRadius: 3,
            overflow: 'auto',
            padding: '2%',
          }}
        >
          <MCustomerDetail selectCus={selectRoomMCus?.mCustomer ?? null} />
        </Flex>
      </Flex>
    </div>
  );
};
